/* eslint no-case-declarations: "off" */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Search } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  CircularProgress,
  createFilterOptions,
  Stack,
  TextField,
  type SxProps,
  type Theme,
} from '@mui/material';
import useServerStateContext from '@/shared/hooks/useServerStateContext';
import { useAutoIsoTeamSelector } from '@/features/teams/context/AutoIsoTeamSelectorContext';
import { useCurrentGame } from '@/features/games/context/CurrentGameContext';
import { TrackedItem } from './TrackedItem';
import { getTeamLogoUrl } from '@/shared/services/getTeamLogoUrl';
import { TeamModel } from '../types/TeamModel';

const filter = createFilterOptions<Player>();

type Player = {
  id: number;
  displayName: string;
  playerNumber: string;
  playerPosition: string;
  category?: string;
  isActive: boolean;
  headshot: string;
  team: {
    id?: number;
    displayName: string;
    color?: string;
    colors?: Array<{ hex_color: string }>;
    logoUrl?: string | null;
  };
};

type Props = {
  value?: number;
  onChange?: (value: number | null) => void;
  readOnly?: boolean;
  disabled?: boolean;
  minimized?: boolean;
  sx?: SxProps<Theme>;
};

const OBJECT_OF_PLAY_ID = 1;

export default function TrackedObject({ value, onChange, readOnly, disabled, sx }: Props) {
  const {
    trackEntity,
    singleCurrentlyTrackedObjectId: selectedId,
    isSwitchingTrackedEntity,
    hasErrorSwitchingTrackedEntity,
  } = useServerStateContext();
  const { processedTeams, handleSearchChange, reset } = useAutoIsoTeamSelector();
  const { homeTeamPrimaryColor, awayTeamPrimaryColor, game, objectOfPlay } = useCurrentGame();

  const OBJECT_OF_PLAY: Player = useMemo(() => ({
    id: OBJECT_OF_PLAY_ID,
    displayName: objectOfPlay || 'Puck',
    playerNumber: '',
    playerPosition: '',
    category: undefined,
    isActive: true,
    headshot: '',
    team: { id: -1, displayName: 'Object of play', color: 'black' },
  }), [objectOfPlay]);

  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Initialize with value prop first, fall back to selectedId from server state
  const [internalValue, setInternalValue] = useState<number | null>(value ?? selectedId ?? null);
  const [inputValue, setInputValue] = useState('');

  // Use ref to store the latest onChange callback to avoid dependency issues
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Track the last selectedId that we've called onChange for
  const lastReportedId = useRef<number | null>(value ?? selectedId ?? null);

  // Cache the last known selected option to prevent disappearing when processedTeams resets
  const cachedSelectedOption = useRef<Player | null>(null);

  const teamColors = useMemo(() => {
    return {
      [game.homeTeam.id]: homeTeamPrimaryColor,
      [game.awayTeam.id]: awayTeamPrimaryColor,
    };
  }, [homeTeamPrimaryColor, awayTeamPrimaryColor, game]);

  const closePopper = useCallback(() => setOpen(false), []);
  const openPopper = useCallback(() => setOpen(true), []);

  // Memoize tracked entities options
  const trackedEntitiesOptions = useMemo(() => {
    const trackedEntities = processedTeams.reduce((acc: Player[], team: TeamModel) => {
      if (team.players) {
        Object.keys(team.players).forEach((category: string) => {
          const players = team.players?.[category as keyof typeof team.players];
          if (Array.isArray(players)) {
            acc.push(...(players as Player[]));
          }
        });
      }
      return acc;
    }, [] as Player[]);

    // Add the special object-of-play row if not already present
    if (!trackedEntities.some((player: Player) => player.id === OBJECT_OF_PLAY.id)) {
      trackedEntities.unshift(OBJECT_OF_PLAY);
    }

    return trackedEntities;
  }, [processedTeams, OBJECT_OF_PLAY]);

  // Function to get team color with proper fallbacks
  const getTeamColor = useCallback(
    (option: Player): string => {
      if (!option || !option.team) return 'black';

      // Try to get color from teamColors using team id
      const teamColorFromMap = option.team.id ? teamColors[option.team.id] : null;

      // If not found, try team.colors array
      const teamColorFromColorsArray = option.team.colors?.[0]?.hex_color;

      // If not found, try team.color direct property
      const teamColorDirect = option.team.color;

      // Return the first available color or default to black
      return teamColorFromMap || teamColorFromColorsArray || teamColorDirect || 'black';
    },
    [teamColors],
  );

  // Memoize team colors for all options
  const teamColorMap = useMemo(() => {
    const colorMap = new Map<number, string>();
    trackedEntitiesOptions.forEach((option: Player) => {
      colorMap.set(option.id, getTeamColor(option));
    });
    return colorMap;
  }, [trackedEntitiesOptions, getTeamColor]);

  const handleTrackingEntityChange = useCallback(
    async (id: number | null) => {
      setError(null);

      try {
        if (id === null) {
          // Handle clearing the selection
          setInternalValue(null);
          if (onChangeRef.current) {
            onChangeRef.current(null);
          }
          lastReportedId.current = null;
        } else {
          // Handle selecting a new entity
          setInternalValue(id);
          await trackEntity({ id }, 'AutoIsoTeamSelector');
        }
      } catch (err) {
        setError('Failed to update tracking entity');
        console.error('Error tracking entity:', err);
      }
    },
    [trackEntity],
  );

  // Handle backend updates - FIXED to prevent infinite loops
  useEffect(() => {
    // Only call onChange if:
    // 1. We're not currently switching
    // 2. The selectedId matches our internal value
    // 3. We have a valid internal value
    // 4. We haven't already reported this selectedId
    if (
      !isSwitchingTrackedEntity &&
      selectedId === internalValue &&
      internalValue !== null &&
      lastReportedId.current !== selectedId
    ) {
      if (onChangeRef.current) {
        onChangeRef.current(selectedId);
        lastReportedId.current = selectedId;
      }
    }
  }, [selectedId, internalValue, isSwitchingTrackedEntity]);

  // Sync with external value changes
  useEffect(() => {
    if (value !== undefined && value !== internalValue) {
      setInternalValue(value);
      lastReportedId.current = value;
    }
  }, [value, internalValue]);

  // Find the current selected option
  const selectedOption = useMemo(() => {
    if (internalValue === null) {
      cachedSelectedOption.current = null;
      return null;
    }

    // Try to find the option in current trackedEntitiesOptions
    const foundOption = trackedEntitiesOptions.find((option: Player) => option.id === internalValue);

    if (foundOption) {
      // Update cache when we find the option
      cachedSelectedOption.current = foundOption;
      return foundOption;
    }

    // If not found in current options but we have a cached version, use it
    // This prevents the UI from disappearing when processedTeams resets
    if (cachedSelectedOption.current && cachedSelectedOption.current.id === internalValue) {
      return cachedSelectedOption.current;
    }

    return null;
  }, [trackedEntitiesOptions, internalValue]);

  useEffect(() => {
    if (readOnly) {
      reset();
    }
  }, [readOnly, reset]);

  // Sync inputValue with selected option
  useEffect(() => {
    if (selectedOption) {
      setInputValue(selectedOption.displayName);
    } else {
      setInputValue('');
    }
  }, [selectedOption]);

  const teamColor = useMemo(() => {
    if (!selectedOption || selectedOption.id === OBJECT_OF_PLAY.id) {
      return 'black'; // Default color for the puck
    }
    return getTeamColor(selectedOption);
  }, [selectedOption, getTeamColor]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Cleanup any pending operations if needed
      setError(null);
    };
  }, []);

  useEffect(() => {
    setError(hasErrorSwitchingTrackedEntity ? 'Failed to switch tracked entity' : null);
  }, [hasErrorSwitchingTrackedEntity]);

  return (
    <Stack spacing={1} sx={sx}>
      {readOnly && selectedOption && (
        <TrackedItem
          id={selectedOption.id}
          displayName={selectedOption.displayName}
          playerNumber={selectedOption.playerNumber}
          playerPosition={selectedOption.playerPosition}
          playerHeadshot={selectedOption.headshot}
          teamLogo={getTeamLogoUrl(selectedOption.team as TeamModel)}
          teamColor={teamColor}
          isTrackedObject={selectedOption.id === OBJECT_OF_PLAY.id}
        />
      )}
      {!readOnly && (
        <Autocomplete
          disabled={isSwitchingTrackedEntity || disabled}
          clearOnEscape
          id="tracking-entity-select"
          open={open}
          onOpen={openPopper}
          onClose={closePopper}
          options={trackedEntitiesOptions}
          value={selectedOption}
          inputValue={inputValue}
          openOnFocus
          disableCloseOnSelect
          getOptionLabel={(option) => option.displayName}
          groupBy={(option) => option.team?.displayName.toUpperCase()}
          style={{ width: 350 }}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderOption={(props, option) => {
            const { key, ...restProps } = props;
            const teamColor = teamColorMap.get(option.id) || 'black';

            return (
              <Box
                component="li"
                {...restProps}
                key={key}
                sx={{
                  cursor: 'pointer',
                }}
                onClick={() => handleTrackingEntityChange(option.id)}
                aria-label={`${option.displayName} from ${option.team.displayName}`}
              >
                <TrackedItem
                  id={option.id}
                  displayName={option.displayName}
                  playerNumber={option.playerNumber}
                  playerPosition={option.playerPosition}
                  playerHeadshot={option.headshot}
                  teamLogo={getTeamLogoUrl(option.team as TeamModel)}
                  teamColor={teamColor}
                  isTrackedObject={option.id === OBJECT_OF_PLAY.id}
                />
              </Box>
            );
          }}
          renderInput={(params) => (
            <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
              <TextField
                {...params}
                id="tracking-entity-search"
                placeholder="Search Player ..."
                size="small"
                error={!!error}
                helperText={error}
                aria-label="Search for a player or puck to track"
                slotProps={{
                  input: {
                    endAdornment: isSwitchingTrackedEntity ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null,
                  },
                }}
              />
              <Search sx={{ color: 'action.active', mr: 1, my: 0.5 }} />
            </Box>
          )}
          onInputChange={(event, value, reason) => {
            setInputValue(value);

            // Clear selection when user starts typing/deleting
            if (reason === 'input' && selectedOption && value !== selectedOption.displayName) {
              setInternalValue(null);
            }

            // Always update search
            handleSearchChange(value);
          }}
          onChange={(event, newValue, reason) => {
            // Handle selection
            handleTrackingEntityChange(newValue ? newValue.id : null);
          }}
          selectOnFocus
          handleHomeEndKeys
          filterOptions={(options, params) => {
            return filter(options, params);
          }}
        />
      )}
    </Stack>
  );
}

export { TrackedObject };
